import { useMemo } from 'react';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { css, StyleSheet } from 'aphrodite';
import cn from 'classnames';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

/* eslint-disable no-restricted-syntax -- Semantic status colors (success/warning/danger) must remain consistent across themes for clarity */
const STATUS_COLORS = {
  success: { accent: '#10B981', accentBg: 'rgba(16, 185, 129, 0.1)' },
  warning: { accent: '#F59E0B', accentBg: 'rgba(245, 158, 11, 0.1)' },
  danger: { accent: '#EF4444', accentBg: 'rgba(239, 68, 68, 0.1)' },
  default: { accent: '#3B82F6', accentBg: 'rgba(59, 130, 246, 0.1)' },
  neutral: '#6B7280'
};
/* eslint-enable no-restricted-syntax */

export interface StatWidgetProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  icon?: React.ReactNode;
  href?: string;
  onClick?: () => void;
  variant?: 'default' | 'success' | 'warning' | 'danger';
  loading?: boolean;
}

export default function StatWidget({
  title,
  value,
  subtitle,
  trend,
  trendValue,
  icon,
  onClick,
  variant = 'default',
  loading = false
}: StatWidgetProps) {
  const { getColour } = useThemeContext();

  const variantColors = useMemo(() => {
    return STATUS_COLORS[variant] || STATUS_COLORS.default;
  }, [variant]);

  const trendIcon = useMemo(() => {
    if (!trend) return null;
    switch (trend) {
      case 'up':
        return (
          <TrendingUp
            className="h-3 w-3"
            style={{ color: STATUS_COLORS.success.accent }}
          />
        );
      case 'down':
        return (
          <TrendingDown
            className="h-3 w-3"
            style={{ color: STATUS_COLORS.danger.accent }}
          />
        );
      default:
        return (
          <Minus className="h-3 w-3" style={{ color: STATUS_COLORS.neutral }} />
        );
    }
  }, [trend]);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        card: {
          ':hover': {
            borderColor: variantColors.accent
          }
        },
        iconContainer: {
          backgroundColor: variantColors.accentBg,
          color: variantColors.accent
        }
      }),
    [variantColors]
  );

  if (loading) {
    return (
      <div
        className={cn(
          'rounded-2xl border border-hairline bg-app-soft shadow-card p-4 animate-pulse',
          css(styles.card)
        )}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div
              className="h-4 rounded-2xl w-24 mb-2"
              style={{ backgroundColor: getColour('border') }}
            />
            <div
              className="h-8 rounded-2xl w-16 mb-1"
              style={{ backgroundColor: getColour('border') }}
            />
            <div
              className="h-3 rounded-2xl w-32"
              style={{ backgroundColor: getColour('border') }}
            />
          </div>
          <div
            className="h-10 w-10 rounded-2xl"
            style={{ backgroundColor: getColour('border') }}
          />
        </div>
      </div>
    );
  }

  const content = (
    <div
      className={cn(
        'rounded-2xl border border-hairline bg-app-soft shadow-card p-4 transition-all duration-200',
        onClick && 'cursor-pointer hover:shadow-card-hover',
        css(styles.card)
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          {/* Title */}
          <p
            className="text-xs font-medium uppercase tracking-wide mb-1 truncate"
            style={{ color: getColour('inactiveText') }}
          >
            {title}
          </p>

          {/* Value */}
          <p
            className="text-2xl font-bold mb-1 truncate"
            style={{ color: getColour('normalText') }}
          >
            {value}
          </p>

          {/* Subtitle & Trend */}
          <div className="flex items-center gap-2">
            {subtitle && (
              <span
                className="text-xs truncate"
                style={{ color: getColour('inactiveText') }}
              >
                {subtitle}
              </span>
            )}
            {trend && trendValue && (
              <span className="flex items-center gap-1 text-xs font-medium">
                {trendIcon}
                <span
                  style={{
                    color:
                      trend === 'up'
                        ? STATUS_COLORS.success.accent
                        : trend === 'down'
                          ? STATUS_COLORS.danger.accent
                          : STATUS_COLORS.neutral
                  }}
                >
                  {trendValue}
                </span>
              </span>
            )}
          </div>
        </div>

        {/* Icon */}
        {icon && (
          <div
            className={cn(
              'flex-shrink-0 rounded-2xl p-2.5 ml-3',
              css(styles.iconContainer)
            )}
          >
            {icon}
          </div>
        )}
      </div>
    </div>
  );

  return content;
}
